// Table view: spreadsheet-style list with combinable filters, presets (my / urgent / production /
// queue), sorting, paging, multi-select bulk actions and Excel export. URL is the source of truth for
// filters so dashboard tiles + board headers can deep-link here.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { WF_APPROVAL_LABELS, WF_REQ_LABELS, WF_REQ_STATUSES, type ClinicOption, type WfCase, type WfCaseListQuery } from '@alignova/shared';
import { useCan } from '../../auth/useCan.ts';
import { useLiveEvent } from '../../lib/useLive.ts';
import { exportUrl, useWfConfig, wfApi } from './workflowApi.ts';
import { CaseActionModal, type ActionTarget, type WfAction } from './WfActions.tsx';
import { AgeBadge, Btn, CaseCard, DoneTag, DueBadge, Empty, Field, Modal, PriorityPill, StagePill, inputCls, fmtD } from './wfUi.tsx';
import { Sheet, SkeletonList, useIsPhone } from '../../ui/mobile.tsx';
import { EmptyState } from '../../ui/primitives.tsx';
import { IconArrowLeft, IconChevronRight } from '../../ui/icons.tsx';

const PAGE = 50;

function parse(sp: URLSearchParams): WfCaseListQuery & { preset?: string } {
  const arr = (k: string) => sp.get(k)?.split(',').filter(Boolean);
  const preset = sp.get('preset') ?? undefined;
  const q: WfCaseListQuery & { preset?: string } = {
    preset,
    search: sp.get('search') ?? undefined,
    stage: arr('stage'),
    group: sp.get('group') ?? undefined,
    caseType: arr('caseType'),
    doctor: sp.get('doctor') ?? undefined,
    clinic: sp.get('clinic') ?? undefined,
    assignedTo: sp.get('assignedTo') ?? undefined,
    priority: arr('priority') as WfCaseListQuery['priority'],
    receivedThrough: arr('receivedThrough'),
    ipr: arr('ipr') as WfCaseListQuery['ipr'],
    etching: arr('etching') as WfCaseListQuery['etching'],
    approval: arr('approval') as WfCaseListQuery['approval'],
    due: (sp.get('due') as WfCaseListQuery['due']) ?? undefined,
    dueFrom: sp.get('dueFrom') ?? undefined,
    dueTo: sp.get('dueTo') ?? undefined,
    receivedFrom: sp.get('receivedFrom') ?? undefined,
    receivedTo: sp.get('receivedTo') ?? undefined,
    // The plain list shows completed / cancelled cases too (tagged); work queues - presets, due,
    // stuck, group - stay live-only so they match the dashboard numbers that link to them.
    includeTerminal:
      sp.get('includeTerminal') === '1' ||
      (sp.get('includeTerminal') !== '0' && !preset && !sp.get('due') && !sp.get('stuck') && !sp.get('group')),
    stuck: sp.get('stuck') === '1',
    sort: (sp.get('sort') as WfCaseListQuery['sort']) ?? 'received',
    dir: (sp.get('dir') as WfCaseListQuery['dir']) ?? 'desc',
    offset: Number(sp.get('offset') ?? 0),
    limit: PAGE,
  };
  if (preset === 'my') q.assignedTo = 'me';
  if (preset === 'urgent') {
    q.due = q.due ?? 'overdue';
    q.sort = 'due';
    q.dir = 'asc';
  }
  if (preset === 'production') q.group = q.group ?? 'production';
  return q;
}

export function WfCasesPage() {
  const nav = useNavigate();
  const can = useCan();
  const cfg = useWfConfig();
  const [sp, setSp] = useSearchParams();
  const q = useMemo(() => parse(sp), [sp]);
  const [items, setItems] = useState<WfCase[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [target, setTarget] = useState<ActionTarget | null>(null);
  const [bulk, setBulk] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  // Search lives in the workflow toolbar — one field, in the same place on every screen. It writes
  // ?search= straight to the URL, which is what this list reads.

  const set = (patch: Record<string, unknown>) => {
    const n = new URLSearchParams(sp);
    for (const [k, v] of Object.entries(patch)) {
      if (v === undefined || v === null || v === '' || v === false || (Array.isArray(v) && !v.length)) n.delete(k);
      else n.set(k, Array.isArray(v) ? v.join(',') : v === true ? '1' : String(v));
    }
    if (!('offset' in patch)) n.delete('offset');
    setSp(n, { replace: true });
  };

  const load = useCallback(() => {
    setLoading(true);
    const { preset: _p, ...rest } = q;
    void _p;
    wfApi
      .list(rest)
      .then((r) => {
        setItems(r.items);
        setTotal(r.total);
        setErr(null);
      })
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, [q]);
  useEffect(load, [load]);
  useLiveEvent('workflow.changed', load);

  const toggle = (id: string, on: boolean) => setSelected((s) => { const n = new Set(s); if (on) n.add(id); else n.delete(id); return n; });
  const allSelected = items.length > 0 && items.every((c) => selected.has(c.id));
  const onAction = (action: WfAction, c: WfCase) => setTarget({ action, c });
  const sortBy = (col: NonNullable<WfCaseListQuery['sort']>) => set({ sort: col, dir: q.sort === col && q.dir === 'desc' ? 'asc' : 'desc' });
  // Sentence case, not SHOUTED SMALL CAPS: twelve uppercase words across the top of a table is
  // texture, not information. The sort arrow only renders on the active column.
  const Th = ({ col, children }: { col?: NonNullable<WfCaseListQuery['sort']>; children: React.ReactNode }) => (
    <th className="whitespace-nowrap px-3 py-2.5 text-left text-[12px] font-semibold text-muted">
      {col ? (
        <button onClick={() => sortBy(col)} className="inline-flex items-center gap-1 rounded transition hover:text-ink">
          {children}
          {q.sort === col && <span className="text-accent">{q.dir === 'asc' ? '↑' : '↓'}</span>}
        </button>
      ) : (
        children
      )}
    </th>
  );
  const phone = useIsPhone();
  const activeFilterCount = [q.stage?.length, q.group, q.caseType?.length, q.doctor, q.clinic, q.assignedTo, q.priority?.length, q.receivedThrough?.length, q.ipr?.length, q.etching?.length, q.approval?.length, q.due, q.dueFrom, q.dueTo, q.receivedFrom, q.receivedTo, q.stuck, sp.get('includeTerminal') === '0'].filter(Boolean).length;
  const title = q.preset === 'my' ? 'My work' : q.preset === 'urgent' ? 'Urgent - overdue & due' : q.preset === 'production' ? 'Production queue' : q.stage?.length === 1 ? cfg.stageName(q.stage[0]) : 'Cases';

  return (
    <div className="space-y-3 pt-1">
      <div className="flex flex-col gap-2 lg:flex-row lg:flex-wrap lg:items-center">
        <div className="flex items-baseline gap-2">
          <h1 className="text-[1.4rem] font-bold tracking-tight text-ink lg:text-xl lg:font-semibold">{title}</h1>
          <span className="tabnum text-sm text-muted">{total} cases</span>
        </div>
        <div className="flex flex-wrap items-center gap-2 lg:ml-auto">
          <Btn tone="ghost" className="flex-1 py-2 lg:h-9 lg:flex-none lg:py-0" onClick={() => setShowFilters(!showFilters)}>
            Filters
            {activeFilterCount > 0 && (
              <span className="tabnum ml-1 rounded-full bg-accent px-1.5 text-[11px] font-bold text-accent-fg">
                {activeFilterCount}
              </span>
            )}
          </Btn>
          {activeFilterCount > 0 && (
            <Btn tone="subtle" className="flex-1 py-2 lg:h-9 lg:flex-none lg:py-0" onClick={() => nav('/workflow/cases')}>
              Clear
            </Btn>
          )}
          {can('workflow:export') && (
            <a
              className="tap hidden h-9 items-center rounded-md border border-line px-3 text-sm text-ink transition hover:bg-surface-2 lg:inline-flex"
              href={exportUrl({ ...q, limit: 5000, ids: selected.size ? [...selected].join(',') : undefined })}
            >
              Export {selected.size ? `${selected.size} selected` : 'Excel'}
            </a>
          )}
        </div>
      </div>

      {/* The filter panel is eighteen controls. Inline it on a desk; on a phone it belongs in a
          sheet, or it buries the list it is meant to filter. Rendered once either way - two live
          copies would duplicate the doctor-code <datalist> id. */}
      {!phone && showFilters && <FilterPanel q={q} set={set} />}
      {phone && (
        <Sheet open={showFilters} onClose={() => setShowFilters(false)} title="Filter cases">
          <div className="pt-1">
            <FilterPanel q={q} set={set} />
          </div>
        </Sheet>
      )}

      {selected.size > 0 && can('workflow:bulk') && (
        <div className="flex flex-wrap items-center gap-2 rounded border border-accent/40 bg-accent/5 px-3 py-2 text-sm">
          <span className="font-medium">{selected.size} selected</span>
          <Btn tone="ghost" onClick={() => setBulk('assign')}>Assign</Btn>
          <Btn tone="ghost" onClick={() => setBulk('priority')}>Priority</Btn>
          <Btn tone="ghost" onClick={() => setBulk('move')}>Move stage</Btn>
          <Btn tone="ghost" onClick={() => setBulk('note')}>Add note</Btn>
          <Btn tone="subtle" onClick={() => setSelected(new Set())}>Clear selection</Btn>
        </div>
      )}

      {err && <div className="text-sm text-status-urgent-fg">{err}</div>}

      {/* Sixteen columns do not become sixteen readable label/value rows on a phone. The board's
          own case card already carries what matters - name, group ID, stage, due, actions. */}
      <div className="space-y-2 lg:hidden">
        {loading && !items.length && <SkeletonList rows={4} />}
        {items.map((c) => (
          <CaseCard
            key={c.id}
            c={c}
            onAction={(action, cc) => onAction(action as WfAction, cc)}
            selected={selected.has(c.id)}
            onSelect={can('workflow:bulk') ? toggle : undefined}
          />
        ))}
        {!loading && !items.length && <Empty>No cases match.</Empty>}
      </div>

      <div className="hidden overflow-hidden rounded-lg border border-line bg-surface shadow-level-1 lg:block">
        <table className="table-cards w-full text-sm">
          <thead className="border-b border-line bg-surface-2">
            <tr>
              <th className="w-8 px-2 py-2"><input type="checkbox" checked={allSelected} onChange={(e) => setSelected(e.target.checked ? new Set(items.map((c) => c.id)) : new Set())} /></th>
              <Th col="patient">Patient</Th>
              <Th>Type</Th>
              <Th col="stage_age">Stage</Th>
              <Th col="due">Due</Th>
              <Th col="received">Received</Th>
              <Th>Assigned</Th>
              <Th>Flags</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr
                key={c.id}
                className={`group border-t border-line transition-colors hover:bg-surface-2/50 ${
                  c.isOverdue ? 'bg-status-urgent-bg/15' : ''
                }`}
              >
                {/* A coloured edge marks the row without competing with the data inside it; the
                    pill beside the name carries the same fact in words. */}
                <td className="relative py-2 pl-3 pr-2 align-top">
                  <span
                    aria-hidden="true"
                    className={`absolute inset-y-0 left-0 w-[3px] ${
                      c.priority === 'emergency'
                        ? 'bg-status-urgent-fg'
                        : c.priority === 'urgent'
                          ? 'bg-status-warn-fg/70'
                          : c.isOverdue
                            ? 'bg-status-urgent-fg/50'
                            : 'bg-transparent'
                    }`}
                  />
                  <input type="checkbox" checked={selected.has(c.id)} onChange={(e) => toggle(c.id, e.target.checked)} />
                </td>
                {/* Identity: who, which case, whose group - one block, since they are always read together. */}
                <td className="max-w-[16rem] px-2 py-2 align-top">
                  {/* The name as it was given, not force-uppercased: caps make a long Urdu or Arabic
                      name wider, harder to scan, and it reads as shouting in a list of people. */}
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/workflow/cases/${c.id}`}
                      className="min-w-0 truncate font-semibold leading-tight text-ink hover:underline"
                      title={c.patientName}
                    >
                      {c.patientName}
                    </Link>
                    <PriorityPill p={c.priority} />
                    <DoneTag stageKey={c.stageKey} />
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 whitespace-nowrap text-[11px] text-muted">
                    {c.groupId && <span className="font-mono font-semibold text-ink">{c.groupId}</span>}
                    <span className="font-mono">{c.caseNumber}</span>
                    {c.phone && <span className="tabnum">{c.phone}</span>}
                  </div>
                  {note(c) && (
                    <div className="mt-0.5 truncate text-[11px] text-ink/70" title={note(c)!}>
                      {note(c)}
                    </div>
                  )}
                </td>
                <td className="max-w-[10rem] px-2 py-2 align-top">
                  <div className="truncate">{c.caseTypeRaw ?? c.caseTypes.map((t) => cfg.optionLabel('case_type', t)).join(', ')}</div>
                  <div className="truncate text-[11px] text-muted">{cfg.optionLabel('received_through', c.receivedThrough)}</div>
                </td>
                {/* Stage and how long it has sat there - the second is meaningless without the first. */}
                <td className="whitespace-nowrap px-2 py-2 align-top">
                  <span className="inline-flex items-center gap-1.5">
                    <StagePill stageKey={c.stageKey} small />
                    <AgeBadge c={c} />
                  </span>
                </td>
                <td className="px-2 py-2 align-top"><DueBadge c={c} /></td>
                <td className="tabnum whitespace-nowrap px-2 py-2 align-top text-xs">{fmtD(c.receivedAt)}</td>
                <td className="max-w-[8rem] px-2 py-2 align-top text-xs">
                  <span className="block truncate">{c.assignedToName ?? <span className="text-muted">-</span>}</span>
                </td>
                {/* Everything that gates the case, as chips rather than three sparse columns. */}
                <td className="px-2 py-2 align-top">
                  <div className="flex flex-wrap gap-1">
                    {c.iprStatus !== 'not_required' && <Flag>IPR: {WF_REQ_LABELS[c.iprStatus]}</Flag>}
                    {c.etchingStatus !== 'not_required' && <Flag>Etch: {WF_REQ_LABELS[c.etchingStatus]}</Flag>}
                    {c.approvalState !== 'none' && <Flag>PT: {WF_APPROVAL_LABELS[c.approvalState]}</Flag>}
                    {c.reprintCount > 0 && <Flag tone="bad">Reprint x{c.reprintCount}</Flag>}
                  </div>
                </td>
                <td className="px-2 py-2 align-top">
                  <div className="flex gap-1 opacity-60 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
                    <RowBtn onClick={() => onAction('move', c)}>Move</RowBtn>
                    <RowBtn onClick={() => onAction('note', c)}>Note</RowBtn>
                    <RowBtn onClick={() => onAction(c.stageKey === 'on_hold' ? 'resume' : 'hold', c)}>{c.stageKey === 'on_hold' ? 'Resume' : 'Hold'}</RowBtn>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {/* Skeleton rows rather than a spinner: the page keeps its shape while the query runs. */}
        {loading && !items.length && (
          <div className="space-y-2 p-3" role="status" aria-label="Loading cases">
            {Array.from({ length: 6 }, (_, i) => (
              <div key={i} className="skeleton h-10 w-full" />
            ))}
          </div>
        )}
        {!loading && !items.length && (
          <div className="px-6 py-12">
            <EmptyState
              title={activeFilterCount || q.search ? 'No cases match these filters' : 'No cases yet'}
              body={
                activeFilterCount || q.search
                  ? 'Widen the search, or clear the filters to see everything.'
                  : 'Cases appear here as clinics send them in, or when you add one.'
              }
              action={
                activeFilterCount || q.search ? (
                  <Btn tone="ghost" onClick={() => nav('/workflow/cases')}>
                    Clear filters
                  </Btn>
                ) : undefined
              }
            />
          </div>
        )}
      </div>
      {total > 0 && (
        <div className="flex items-center justify-between gap-3 text-sm text-muted">
          <span className="tabnum">
            Showing {items.length ? (q.offset ?? 0) + 1 : 0}–{(q.offset ?? 0) + items.length} of {total}
          </span>
          <div className="flex gap-2">
            <Btn
              tone="ghost"
              className="h-9 gap-1.5 py-0"
              disabled={(q.offset ?? 0) === 0}
              onClick={() => set({ offset: Math.max(0, (q.offset ?? 0) - PAGE) })}
            >
              <IconArrowLeft size={16} />
              Previous
            </Btn>
            <Btn
              tone="ghost"
              className="h-9 gap-1.5 py-0"
              disabled={(q.offset ?? 0) + PAGE >= total}
              onClick={() => set({ offset: (q.offset ?? 0) + PAGE })}
            >
              Next
              <IconChevronRight size={16} />
            </Btn>
          </div>
        </div>
      )}
      <CaseActionModal target={target} onClose={() => setTarget(null)} onDone={() => { setTarget(null); load(); }} />
      <BulkModal action={bulk} ids={[...selected]} onClose={() => setBulk(null)} onDone={() => { setBulk(null); setSelected(new Set()); load(); }} />
    </div>
  );
}

/** The case's own note, whichever field carries it. */
const note = (c: WfCase): string | null => c.additionalNotes || c.notes || null;

/** A small chip for the gates on a case: IPR, etching, doctor approval, reprints. */
function Flag({ children, tone = 'neutral' }: { children: React.ReactNode; tone?: 'neutral' | 'bad' }) {
  return (
    <span
      className={`whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] ${
        tone === 'bad' ? 'bg-status-urgent-bg text-status-urgent-fg' : 'bg-surface-2 text-ink'
      }`}
    >
      {children}
    </span>
  );
}

function RowBtn(p: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...p}
      className="rounded-md border border-line/70 px-2 py-1 text-[11px] font-medium text-muted transition hover:border-line hover:bg-surface-2 hover:text-ink"
    />
  );
}

function FilterPanel({ q, set }: { q: WfCaseListQuery; set: (p: Record<string, unknown>) => void }) {
  const cfg = useWfConfig();

  // The lab's real clinics, so filtering by one is a choice rather than a spelling test. Loaded
  // once when the panel first opens — the panel is closed most of the time, and a lab has hundreds
  // of clinics, not thousands, so filtering happens in the browser rather than per keystroke.
  const [clinics, setClinics] = useState<ClinicOption[]>([]);
  useEffect(() => {
    wfApi.clinics().then(setClinics).catch(() => undefined);
  }, []);
  const Multi = ({ label, k, options }: { label: string; k: string; options: Array<{ key: string; label: string }> }) => {
    const cur = ((q as Record<string, unknown>)[k] as string[] | undefined) ?? [];
    return (
      <Field label={label}>
        <select multiple className={`${inputCls} h-28`} value={cur} onChange={(e) => set({ [k]: [...e.target.selectedOptions].map((o) => o.value) })}>
          {options.map((o) => (
            <option key={o.key} value={o.key}>
              {o.label}
            </option>
          ))}
        </select>
      </Field>
    );
  };
  return (
    <div className="grid grid-cols-1 gap-x-6 gap-y-3 rounded-lg border border-line bg-surface p-4 shadow-level-1 xl:grid-cols-2">
      <Multi label="Stage" k="stage" options={cfg.stages.filter((s) => s.isActive).map((s) => ({ key: s.key, label: s.name }))} />
      <Multi label="Case type" k="caseType" options={cfg.optionsOf('case_type')} />
      <Multi label="Received through" k="receivedThrough" options={cfg.optionsOf('received_through')} />
      <Multi label="Priority" k="priority" options={[{ key: 'normal', label: 'Normal' }, { key: 'urgent', label: 'Urgent' }, { key: 'emergency', label: 'Emergency' }]} />
      <Multi label="IPR" k="ipr" options={WF_REQ_STATUSES.map((s) => ({ key: s, label: WF_REQ_LABELS[s] }))} />
      <Multi label="Etching" k="etching" options={WF_REQ_STATUSES.map((s) => ({ key: s, label: WF_REQ_LABELS[s] }))} />
      <Field label="Doctor / Group ID">
        <input className={inputCls} list="wf-doctor-codes-f" value={q.doctor ?? ''} onChange={(e) => set({ doctor: e.target.value })} />
        <datalist id="wf-doctor-codes-f">{cfg.doctors.map((d) => <option key={d.id} value={d.code}>{d.name}</option>)}</datalist>
      </Field>
      <Field
        label="Clinic"
        hint={clinics.length ? `${clinics.length} on record - pick one or type any part of a name` : undefined}
      >
        <input
          className={inputCls}
          list="wf-clinic-filter"
          placeholder="Search clinics"
          value={q.clinic ?? ''}
          onChange={(e) => set({ clinic: e.target.value })}
        />
        <datalist id="wf-clinic-filter">
          {clinics.map((c) => (
            <option key={c.id} value={c.name}>
              {[c.city, c.doctorCode, c.caseCount ? `${c.caseCount} cases` : null].filter(Boolean).join(' · ')}
            </option>
          ))}
        </datalist>
      </Field>
      <Field label="Assigned">
        <select className={inputCls} value={q.assignedTo ?? ''} onChange={(e) => set({ assignedTo: e.target.value })}>
          <option value="">Anyone</option>
          <option value="me">Me</option>
          <option value="unassigned">Unassigned</option>
          {cfg.staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </Field>
      <Field label="Due">
        <select className={inputCls} value={q.due ?? ''} onChange={(e) => set({ due: e.target.value })}>
          <option value="">Any</option>
          <option value="overdue">Overdue</option>
          <option value="today">Today</option>
          <option value="tomorrow">Tomorrow</option>
          <option value="week">Next 7 days</option>
        </select>
      </Field>
      <Field label="Due from"><input type="date" className={inputCls} value={q.dueFrom ?? ''} onChange={(e) => set({ dueFrom: e.target.value })} /></Field>
      <Field label="Due to"><input type="date" className={inputCls} value={q.dueTo ?? ''} onChange={(e) => set({ dueTo: e.target.value })} /></Field>
      <Field label="Received from"><input type="date" className={inputCls} value={q.receivedFrom ?? ''} onChange={(e) => set({ receivedFrom: e.target.value })} /></Field>
      <Field label="Received to"><input type="date" className={inputCls} value={q.receivedTo ?? ''} onChange={(e) => set({ receivedTo: e.target.value })} /></Field>
      <Field label="Approval">
        <select className={inputCls} value={q.approval?.[0] ?? ''} onChange={(e) => set({ approval: e.target.value ? [e.target.value] : [] })}>
          <option value="">Any</option>
          {Object.entries(WF_APPROVAL_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </Field>
      <div className="flex flex-col justify-end gap-1 text-sm">
        <label className="flex items-center gap-2"><input type="checkbox" checked={!!q.includeTerminal} onChange={(e) => set({ includeTerminal: e.target.checked ? '1' : '0' })} /> Include completed / cancelled</label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={!!q.stuck} onChange={(e) => set({ stuck: e.target.checked })} /> Over SLA only</label>
      </div>
    </div>
  );
}

function BulkModal({ action, ids, onClose, onDone }: { action: string | null; ids: string[]; onClose: () => void; onDone: () => void }) {
  const cfg = useWfConfig();
  const [v, setV] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: string[]; failed: Array<{ id: string; error: string }> } | null>(null);
  const [confirm, setConfirm] = useState(false);
  useEffect(() => { setV(''); setNote(''); setResult(null); setConfirm(false); }, [action]);
  if (!action) return null;
  const run = async () => {
    setBusy(true);
    try {
      const body: Record<string, unknown> = { ids, action };
      if (action === 'assign') body.assignedTo = v || null;
      if (action === 'priority') body.priority = v || 'normal';
      if (action === 'move') body.toStage = v;
      if (action === 'note') body.note = note;
      setResult(await wfApi.bulk(body));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open onClose={onClose} title={`Bulk ${action} - ${ids.length} cases`}>
      {result ? (
        <div className="space-y-2 text-sm">
          <div className="text-status-progress-fg">{result.ok.length} updated</div>
          {result.failed.length > 0 && (
            <div>
              <div className="font-semibold text-status-urgent-fg">{result.failed.length} failed</div>
              <ul className="max-h-48 overflow-y-auto text-xs">{result.failed.map((f) => <li key={f.id}>{f.id.slice(0, 8)}… - {f.error}</li>)}</ul>
            </div>
          )}
          <div className="flex justify-end"><Btn onClick={onDone}>Done</Btn></div>
        </div>
      ) : (
        <div className="space-y-3">
          {action === 'assign' && (
            <Field label="Assign to"><select className={inputCls} value={v} onChange={(e) => setV(e.target.value)}><option value="">- unassigned -</option>{cfg.staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></Field>
          )}
          {action === 'priority' && (
            <Field label="Priority"><select className={inputCls} value={v} onChange={(e) => setV(e.target.value)}><option value="normal">Normal</option><option value="urgent">Urgent</option><option value="emergency">Emergency</option></select></Field>
          )}
          {action === 'move' && (
            <Field label="Move to stage"><select className={inputCls} value={v} onChange={(e) => setV(e.target.value)}><option value="">- choose -</option>{cfg.stages.filter((s) => s.isActive && s.key !== 'on_hold' && s.key !== 'case_cancelled').map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}</select></Field>
          )}
          {action === 'note' && <Field label="Note (added to every selected case)"><textarea className={inputCls} rows={3} value={note} onChange={(e) => setNote(e.target.value)} /></Field>}
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} /> I confirm this change applies to {ids.length} cases</label>
          <div className="flex justify-end gap-2">
            <Btn tone="ghost" onClick={onClose}>Cancel</Btn>
            <Btn tone="accent" disabled={busy || !confirm || (action === 'move' && !v) || (action === 'note' && !note.trim())} onClick={() => void run()}>{busy ? 'Applying…' : 'Apply'}</Btn>
          </div>
        </div>
      )}
    </Modal>
  );
}
